import { NgClass } from '@angular/common';
import { AfterViewInit, Component, inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MessageService, PrimeTemplate, SelectItem } from 'primeng/api';
import { Button, ButtonProps } from 'primeng/button';
import { Card } from 'primeng/card';
import { FileUpload } from 'primeng/fileupload';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { Textarea } from 'primeng/textarea';
import { SERVICE_ENDPOINTS } from '../../../core/configs/service-endpoints';
import { SafeHtmlPipe } from '../../../core/pipes/safe-html.pipe';
import { AuthService } from '../../../core/services/auth.service';
import { FormDataUploadService } from '../../../core/services/form-data-upload.service';
import { HelperService } from '../../../core/services/helper.service';
import { I18nService } from '../../../core/services/i18n.service';
import { SpsSupportInfoComponent } from '../../../custom/components/sps-support-info/sps-support-info.component';
import { LayoutService } from '../../../layout/services/layout.service';
import { ContactData } from '../../interfaces/contact/contact-data';
import { ContactDataInvalidFlags } from '../../interfaces/contact/contact-data-invalid-flags';
import { ContactDataService } from '../../services/contact-data.service';
import { SupportInformationService } from '../../services/support-information.service';
import { BaseComponent } from '../base/base.component';

@Component({
  selector: 'app-help-contact',
  imports: [
    Card,
    SafeHtmlPipe,
    InputText,
    ReactiveFormsModule,
    FormsModule,
    NgClass,
    Textarea,
    Select,
    Button,
    PrimeTemplate,
    SpsSupportInfoComponent
  ],
  templateUrl: './help-contact.component.html'
})
export class HelpContactComponent extends BaseComponent implements OnInit, AfterViewInit {

  public authService = inject(AuthService);
  public layoutService = inject(LayoutService);
  public i18n = inject(I18nService);
  public helper = inject(HelperService);
  public contactService = inject(ContactDataService);
  public supportInfoService = inject (SupportInformationService);
  public formDataUploadService = inject(FormDataUploadService);
  public contactInfo: string;

  /**
   * Contact-data
   */
  contactData: ContactData = null;
  contactDataInvalidFlags: ContactDataInvalidFlags;
  inquiryReasonList: SelectItem[] = [];
  //TODO: Nur auskommentiert und nicht gelöscht, da laut Code Doku (unten) ab dem 1.11.2026 anderweitig genutzt wird
  // contactReset: boolean = false;

  /**
   * reference of the FileUpload
   * @private
   */
  private fileUpload: FileUpload = null;
  maxAttachmentFiles: number = 5;
  filesValid: boolean = true;

  /**
   * set style of cancel-button to primary
   */
  cancelButtonProps: ButtonProps = {
    severity: 'primary',
  };

  constructor(private messageService: MessageService) {
    super();
  }

  ngOnInit() {
    this.loading = true;
    this.initContactData();
    this.initReasonList();
    this.contactService.getContactInfo(this.i18n.activeLanguageKey()).subscribe({
      next: (data) => {
        this.contactInfo = data;
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        console.error(error);
      }
    });
  }

  ngAfterViewInit(): void {
    /**
     * Some controls need refresh after language-changes
     */
    this.subscriptions.add(
      this.i18n.languageChanged.subscribe({
        next: value => {
          this.refreshControls = true;
          this.initReasonList();
          setTimeout(() => {
            this.refreshControls = false;
          }, 10);
        }
      })
    )
    /**
     * Initialize Breadcrumb-Items
     */
    setTimeout(() => {
      this.i18n.breadcrumbMenuItems.set([
        {
          icon: 'pi pi-home',
          i18nLabelKey: 'MENUDASHBOARD',
          label: this.i18n.i18nLng()['MENUDASHBOARD'],
          routerLink: ['/dashboard'],
          routerLinkActive: false,
        },
        {
          i18nLabelKey: 'MENUHELP',
          label: this.i18n.i18nLng()['MENUHELP'],
          routerLinkActive: false,
        },
        {
          i18nLabelKey: 'MENUCONTACT',
          label: this.i18n.i18nLng()['MENUCONTACT'],
          routerLinkActive: true,
        },
      ]);
    }, 50);
  }

  private initContactData() {
    this.contactData = {
      tenantId: this.authService.organisationData.displayCustomerNumber,
      firstName: this.authService.userData.firstName,
      lastName: this.authService.userData.lastName,
      subject: '',
      inquiryReason: null,
      request: '',
      // files: [],
    }
    this.resetInvalidFlags();
  }

  private resetInvalidFlags() {
    this.contactDataInvalidFlags = {
      subject: false,
      inquiryReason: false,
      request: false,
    }
  }

  private initReasonList() {
    this.inquiryReasonList = [
      {
        label: this.i18n.i18nLng()['INQUIRY_GENERAL'],
        value: 'INQUIRY_GENERAL',
      },
      {
        label: this.i18n.i18nLng()['INQUIRY_TECHNICAL'],
        value: 'INQUIRY_TECHNICAL',
      },
      {
        label: this.i18n.i18nLng()['INQUIRY_PAYMENTMETHOD'],
        value: 'INQUIRY_PAYMENTMETHOD',
      },
      {
        label: this.i18n.i18nLng()['INQUIRY_CONTRACT'],
        value: 'INQUIRY_CONTRACT',
      },
    ]
  }

  /**
   * Add attachment file for upload
   * @param event
   * @param fileUpload
   */
  /*
  addAttachmentFile(event: FileSelectEvent, fileUpload: FileUpload) {
    this.fileUpload = fileUpload;
    this.contactData.files.push(...event.files);
    if (this.contactData.files.length > this.maxAttachmentFiles) {
      this.filesValid = false;
    }
  }
  */

  /**
   * Remove attachment file from upload component
   * @param event
   */
  /*
  removeAttachmentFile(event: FileRemoveEvent) {
    this.contactData.files.splice(this.contactData.files.indexOf(event.file), 1);
    if (this.contactData.files.length <= this.maxAttachmentFiles) {
      this.filesValid = true;
    }
  }
  */

  /**
   * Delete all attachment files from upload component
   */
  clearAttachmentFiles() {
    /*
    this.contactData.files = [];
     */
    this.fileUpload = null;
    this.filesValid = true;
  }

  /**
   * Check validity and send inquiry to backend for persistence
   */
  saveInquiry() {
    let valid: boolean = true
    this.resetInvalidFlags();

    /**
     * check contactData validity
     */
    if (!this.contactData?.subject || this.contactData.subject?.trim().length === 0) {
      valid = false;
      this.contactDataInvalidFlags.subject = true;
    }
    if (!this.contactData?.inquiryReason) {
      valid = false;
      this.contactDataInvalidFlags.inquiryReason = true;
    }
    if (!this.contactData?.request || this.contactData.request?.trim().length === 0) {
      valid = false;
      this.contactDataInvalidFlags.request = true;
    }

    // TODO for future use with file-upload:  if (valid && this.filesValid) {
    if (valid) {
      this.layoutService.progressDialogTitle = this.i18n.i18nLng()['SENDINGCONTACT'];
      this.layoutService.showProgressSpinner = true;
      this.layoutService.showProgressDialog = true;

      this.supportInfoService.create(SERVICE_ENDPOINTS.CREATE_SUPPORT_REQUEST, this.contactData).subscribe({
        next: (result) => {
          this.layoutService.showProgressDialog = false;
          if (result.successful) {
            this.messageService.add({
              life: 5000,
              severity: 'success',
              summary: this.i18n.i18nLng()['CONTACTREQUEST'],
              detail: this.i18n.i18nLng()['CONTACTREQUESTSUCCESS'],
            });
            // this.contactReset = true;
            this.initContactData();
          } else {
            this.messageService.add({
              sticky: true,
              severity: 'error',
              summary: this.i18n.i18nLng()['CONTACTREQUESTERROR'],
              detail: this.i18n.i18nLng()['CONTACTREQUESTERRORRETRY'],
            });
          }
        },
        error: (err) => {
          this.layoutService.showProgressDialog = false;
          this.messageService.add({
            sticky: true,
            severity: 'error',
            summary: this.i18n.i18nLng()['CONTACTREQUESTERROR'],
            detail: this.i18n.i18nLng()['CONTACTREQUESTERRORRETRY'],
          });
        }
      });


      /**
       * Build FormData for the inquiry
       * for future use, with attachments
       */
      /*
      const formData = new FormData();
      formData.append('merchantId', this.contactData?.tenantId);
      formData.append('firstName', this.contactData?.firstName || '');
      formData.append('lastName', this.contactData?.lastName || '');
      formData.append('subject', this.contactData?.subject || '');
      formData.append('inquiryReason', this.i18n.i18nLng()[this.contactData.inquiryReason]);
      formData.append('request', this.contactData.request || '');
      /*
      for (const file of this.contactData.files) {
        formData.append('files', file, file.name);
      }
      */

      // TODO save inquiry
      // TODO should be implemented after 01.11.26
      /* uncomment when backend is ready
      const subscription: Subscription = this.formDataUploadService.uploadFormData('/route/zum/bakckend', formData, true, true, 'Anlagen werden hochgeladen').subscribe({
        next: (completed) => {
          subscription.unsubscribe();
          if (completed) {
            this.messageService.add({
              life: 5000,
              severity: 'success',
              summary: this.i18n.i18nLng()['CONTACTREQUEST'],
              detail: this.i18n.i18nLng()['CONTACTREQUESTSUCCESS'],
            });
            this.contactReset = true;
            this.initContactData();
            if (this.fileUpload) {
              this.fileUpload.clear();
              this.fileUpload = null;
            }
            setTimeout(() => {
              this.contactReset = false;
            }, 10);
          } else {
            if (this.formDataUploadService.uploadError) {
              this.messageService.add({
                sticky: true,
                severity: 'error',
                summary: this.i18n.i18nLng()['CONTACTREQUESTERROR'],
                detail: this.formDataUploadService.uploadErrorMsg + '<br>' + this.i18n.i18nLng()['CONTACTREQUESTERRORRETRY'],
              });
            } else {
              this.messageService.add({
                sticky: true,
                severity: 'error',
                summary: this.i18n.i18nLng()['CONTACTREQUESTERROR'],
                detail: this.i18n.i18nLng()['CONTACTREQUESTERRORRETRY'],
              });
            }
          }
        },
      })
       */

      /**
       * Remove when persistence is implemented
       */
      /*
      this.messageService.add({
        life: 5000,
        severity: 'success',
        summary: this.i18n.i18nLng()['CONTACTREQUEST'],
        detail: this.i18n.i18nLng()['CONTACTREQUESTSUCCESS'],
      });
      this.contactReset = true;
      this.initContactData();
       */
      /*
      if (this.fileUpload) {
        this.fileUpload.clear();
        this.fileUpload = null;
      }
       */
      /*
      setTimeout(() => {
        this.contactReset = false;
      }, 10);
       */

    }
  }
}
